import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getErrorMessages,
  useDeleteMarketUpdateMutation,
  useGetInvestmentMarketUpdatesQuery,
  useLazyGetInvestmentMarketUpdatesQuery,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { InvestmentMarketUpdateItem } from "../../../../services/investments/types";
import {
  UseInvestmentMarketUpdatesProps,
  UseInvestmentMarketUpdatesReturn,
} from "./types";
import { PAGE_SIZE } from "../../../../utils/constants";

// Page 0 stays a subscribed query so it auto-refetches on invalidation; extra pages are appended
// locally and reset whenever the base page refetches, to stay in sync with it.
export const useInvestmentMarketUpdates = ({
  id,
  onChanged,
}: UseInvestmentMarketUpdatesProps): UseInvestmentMarketUpdatesReturn => {
  const base = useGetInvestmentMarketUpdatesQuery({
    id,
    page: 0,
    limit: PAGE_SIZE,
  });
  const [fetchMore, lazyResult] = useLazyGetInvestmentMarketUpdatesQuery();
  const [deleteMarketUpdate] = useDeleteMarketUpdateMutation();

  const [appended, setAppended] = useState<InvestmentMarketUpdateItem[]>([]);
  const [nextPage, setNextPage] = useState(1);
  const [latestPagination, setLatestPagination] = useState(
    base.data?.pagination,
  );

  useEffect(() => {
    setAppended([]);
    setNextPage(1);
    setLatestPagination(base.data?.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.fulfilledTimeStamp, id]);

  const notifyErrors = useCallback((err: any): string => {
    notify.apiError(err);
    return getErrorMessages(err)[0] ?? "Failed to load market updates";
  }, []);

  const items = useMemo(
    () => [...(base.data?.items ?? []), ...appended],
    [base.data?.items, appended],
  );

  const pagination = latestPagination ?? base.data?.pagination;

  const loadMore = useCallback(async () => {
    if (lazyResult.isFetching || !pagination?.hasNext) {
      return;
    }
    try {
      const result = await fetchMore({
        id,
        page: nextPage,
        limit: PAGE_SIZE,
      }).unwrap();
      setAppended((prev) => [...prev, ...result.items]);
      setLatestPagination(result.pagination);
      setNextPage((prev) => prev + 1);
    } catch (err) {
      notifyErrors(err);
    }
  }, [
    fetchMore,
    id,
    nextPage,
    pagination?.hasNext,
    lazyResult.isFetching,
    notifyErrors,
  ]);

  const reload = useCallback(async () => {
    await base.refetch();
  }, [base]);

  const removeMarketUpdate = useCallback(
    async (item: InvestmentMarketUpdateItem) => {
      try {
        await deleteMarketUpdate({ id, marketUpdateId: item.id }).unwrap();
        notify.success("Market update deleted");
        onChanged?.();
      } catch {
        // Global middleware shows the toast; nothing else to do here.
      }
    },
    [id, deleteMarketUpdate, onChanged],
  );

  const error = base.error
    ? (getErrorMessages(base.error as any)[0] ??
      "Failed to load market updates")
    : null;

  return {
    items,
    totalRows: pagination?.totalRows ?? 0,
    hasNext: pagination?.hasNext ?? false,
    isLoading: base.isLoading || (base.isFetching && !base.data),
    isLoadingMore: lazyResult.isFetching,
    error,
    loadMore,
    removeMarketUpdate,
    reload,
  };
};
